import { ArrowLeftRight } from "lucide-react";
import { useId } from "react";
import { useMessages, type Messages } from "../i18n";
import {
  LANGUAGE_CODES,
  LANGUAGE_NATIVE_LABELS,
  type LanguageCode,
} from "../shared";

/**
 * How a language is offered: its own name, then its name in the language the
 * reader is reading. One entry when those are the same word, which is what
 * happens to English on the English site and to Dutch on the Dutch one.
 */
function optionLabel(code: LanguageCode, t: Messages): string {
  const native = LANGUAGE_NATIVE_LABELS[code];
  const known = t.languages[code];

  return native === known ? known : `${native} · ${known}`;
}

interface LanguagePairProps {
  left: LanguageCode;
  right: LanguageCode;
  leftLabel?: string;
  rightLabel?: string;
  onChange: (pair: { left: LanguageCode; right: LanguageCode }) => void;
}

/**
 * Picking a language that is already on the other side swaps the two rather
 * than rejecting the choice, so the control can never sit in an invalid state.
 */
export function LanguagePair({
  left,
  right,
  leftLabel,
  rightLabel,
  onChange,
}: LanguagePairProps) {
  const leftId = useId();
  const rightId = useId();
  const t = useMessages();

  return (
    <div className="pair">
      <div className="pair__field pair__field--left">
        <label htmlFor={leftId}>{leftLabel ?? t.app.leftSide}</label>
        <select
          id={leftId}
          value={left}
          onChange={(event) => {
            const next = event.target.value as LanguageCode;
            onChange({ left: next, right: next === right ? left : right });
          }}
        >
          {LANGUAGE_CODES.map((code) => (
            <option key={code} value={code}>
              {optionLabel(code, t)}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        className="pair__swap"
        onClick={() => onChange({ left: right, right: left })}
        aria-label={t.app.swapSides}
      >
        <ArrowLeftRight size={17} aria-hidden="true" />
      </button>

      <div className="pair__field pair__field--right">
        <label htmlFor={rightId}>{rightLabel ?? t.app.rightSide}</label>
        <select
          id={rightId}
          value={right}
          onChange={(event) => {
            const next = event.target.value as LanguageCode;
            onChange({ left: next === left ? right : left, right: next });
          }}
        >
          {LANGUAGE_CODES.map((code) => (
            <option key={code} value={code}>
              {optionLabel(code, t)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
